import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useField } from "formik";
import {
  AssetType,
  InterventionType,
  AssessmentMethodology,
  MappedDropDownOptions,
} from "../solution/types";

export type Options =
  | Array<AssetType>
  | Array<InterventionType>
  | Array<MappedDropDownOptions<AssessmentMethodology>>;

export type FieldPath =
  | "solution-intervention-type"
  | "solution-assessment-methodology"
  | "programme-info.asset-type";

export type SingleSelectProps = {
  options: Options;
  name: string;
  label: string;
  fieldPath: FieldPath;
  warnInsteadOfError: boolean;
  value?: string | null;
};

type SelectValue = AssetType | InterventionType | AssessmentMethodology | "";

const getMappedValues = (
  option: SelectValue | MappedDropDownOptions<AssessmentMethodology>,
) => {
  if (typeof option === "string") return { value: option, label: option };
  return option;
};

function SingleSelect({
  options,
  name,
  label,
  fieldPath,
  warnInsteadOfError = false,
  value,
}: SingleSelectProps) {
  const [field, meta, helpers] = useField<SelectValue>(fieldPath);
  const hasValidationMessage = meta.touched && Boolean(meta.error);

  const handleChange = (event: SelectChangeEvent<SelectValue>) => {
    helpers.setValue(event.target.value);
  };

  const validationSeverity =
    warnInsteadOfError && !value?.length ? "warning" : undefined;

  const validationMessage = meta.error;

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={hasValidationMessage}
      data-validation-severity={validationSeverity}
    >
      <InputLabel id={`solution-${name}-label`} variant="standard">
        {label}
      </InputLabel>

      <Select
        id={`solution-${name}`}
        labelId={`solution-${name}-label`}
        name={fieldPath}
        variant="standard"
        label={label}
        value={field.value ?? ""}
        onChange={handleChange}
      >
        {options.map((option) => {
          const mappedOption = getMappedValues(option);

          return (
            <MenuItem key={mappedOption.value} value={mappedOption.value}>
              {mappedOption.label}
            </MenuItem>
          );
        })}
      </Select>

      {hasValidationMessage && (
        <FormHelperText>{validationMessage}</FormHelperText>
      )}
    </FormControl>
  );
}

export default SingleSelect;
